/**
 * Instant hide/show of opted-in MainContent sections.
 *
 * Opt-in only: data-scroll-transition="true" and/or .scroll-transition-section
 * Never selects the header, announcement bar, or footer.
 * Never uses document.querySelectorAll('section').
 * Native scrollbar stays visible. One wheel/swipe gesture moves exactly one section.
 */
(function () {
  'use strict';

  var HEADER_EXCLUSION_SELECTOR = [
    '#shopify-section-group-header-group',
    '#shopify-section-header',
    '#shopify-section-announcement-bar',
    '.shopify-section-group-header-group',
    '.shopify-section-header',
    '.section-header',
    '.announcement-bar-section',
    'sticky-header',
    '.header-wrapper',
    '#shopify-section-group-footer-group',
    '.shopify-section-group-footer-group',
  ].join(',');

  var SCROLLABLE_IGNORE_SELECTOR = [
    'textarea',
    'select',
    'input',
    '[role="dialog"]',
    'dialog',
    'cart-drawer',
    'menu-drawer',
    'details[open]',
    '.drawer',
    '.modal-overlay',
    'predictive-search',
    '[data-scroll-lock-ignore]',
  ].join(',');

  var LOCK_MS = 800;
  var DESKTOP_WHEEL_IDLE_MS = 160;
  var SWIPE_THRESHOLD = 40;
  var LONG_SCROLL_IDLE_MS = 180;

  var main = null;
  var sections = [];
  var transitionFrame = 0;
  var resizePending = false;
  var active = false;
  var locked = false;
  var lockTimer = 0;
  var desktopWheelIndex = null;
  var desktopWheelLastAt = 0;
  var currentIndex = 0;
  var appliedIndex = null;
  var appliedEnding = null;
  var touchState = null;
  var nativeLongScroll = null;
  var nativeLongIdleTimer = 0;
  var listenersBound = false;
  var shopifyBound = false;
  var editorMobileQuery = null;
  var mobileHomeQuery = window.matchMedia('(max-width: 767px)');

  function getMainContent() {
    return document.getElementById('MainContent');
  }

  function isExcludedChrome(el) {
    if (!el || el.nodeType !== 1) return true;
    if (el.closest && el.closest(HEADER_EXCLUSION_SELECTOR)) return true;

    var id = el.id || '';
    var className = typeof el.className === 'string' ? el.className : '';

    if (id === 'shopify-section-header' || id === 'shopify-section-announcement-bar') return true;
    if (id === 'shopify-section-group-header-group' || id === 'shopify-section-group-footer-group') return true;
    if (className.indexOf('section-header') !== -1) return true;
    if (className.indexOf('shopify-section-header') !== -1) return true;
    if (className.indexOf('announcement-bar-section') !== -1) return true;
    if (className.indexOf('shopify-section-group-header-group') !== -1) return true;
    if (className.indexOf('shopify-section-group-footer-group') !== -1) return true;

    return false;
  }

  function wrapperForOptIn(el, root) {
    if (!el || !root) return null;
    if (el.parentElement === root) return el;

    var node = el.parentElement;
    while (node && node !== root) {
      if (node.parentElement === root) return node;
      node = node.parentElement;
    }

    return null;
  }

  function collectTransitionSections(root) {
    if (!root) return [];

    var found = [];
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

    function add(el) {
      if (!el || isExcludedChrome(el)) return;
      if (el.parentElement !== root) return;
      if (seen) {
        if (seen.has(el)) return;
        seen.add(el);
      } else if (found.indexOf(el) !== -1) {
        return;
      }
      found.push(el);
    }

    var direct = document.querySelectorAll(
      '#MainContent > .scroll-transition-section:not(.shopify-section-header):not(.section-header):not(.announcement-bar-section), ' +
        '#MainContent > [data-scroll-transition="true"]'
    );
    for (var i = 0; i < direct.length; i++) add(direct[i]);

    var optedIn = root.querySelectorAll('[data-scroll-transition="true"]');
    for (var j = 0; j < optedIn.length; j++) {
      var optIn = optedIn[j];
      if (isExcludedChrome(optIn)) continue;

      var wrapper = wrapperForOptIn(optIn, root);
      if (!wrapper || isExcludedChrome(wrapper)) continue;

      wrapper.classList.add('scroll-transition-section');
      if (wrapper.getAttribute('data-scroll-transition') !== 'true') {
        wrapper.setAttribute('data-scroll-transition', 'true');
      }
      add(wrapper);
    }

    return found;
  }

  function resetVisual(wrapper) {
    wrapper.classList.remove('is-scroll-section-active');
    wrapper.removeAttribute('aria-hidden');
  }

  function documentTop(el) {
    return (window.pageYOffset || window.scrollY || 0) + el.getBoundingClientRect().top;
  }

  function stackStart() {
    return sections.length ? documentTop(sections[0]) : 0;
  }

  function stackEnd() {
    if (!sections.length) return 0;
    var last = sections[sections.length - 1];
    return documentTop(last) + last.offsetHeight;
  }

  function lastSectionTop() {
    if (!sections.length) return 0;
    return documentTop(sections[sections.length - 1]);
  }

  /**
   * 获取文档实际可滚动到的最底部位置，兼容页脚高度小于过渡区总高度的情况。
   * @returns {number} 当前文档的最大滚动距离。
   */
  function documentScrollMax() {
    var rootHeight = document.documentElement ? document.documentElement.scrollHeight : 0;
    var bodyHeight = document.body ? document.body.scrollHeight : 0;
    var documentHeight = Math.max(rootHeight, bodyHeight);
    return Math.max(0, documentHeight - (window.innerHeight || 1));
  }

  function isInsideStack() {
    if (currentIndex >= sections.length - 1) return false;
    var scrollY = window.pageYOffset || window.scrollY || 0;
    return scrollY >= stackStart() - 2 && scrollY < lastSectionTop() - 2;
  }

  function isAtLastSectionTop() {
    if (!sections.length) return false;
    var scrollY = window.pageYOffset || window.scrollY || 0;
    return Math.abs(scrollY - lastSectionTop()) < 24;
  }

  function isNearStackEnd() {
    var scrollY = window.pageYOffset || window.scrollY || 0;
    var maxScroll = documentScrollMax();
    return maxScroll > 0 && scrollY >= maxScroll - 32;
  }

  /** 判断是否使用桌面滚轮保护；移动端和平板继续使用原有手势逻辑。 */
  function isDesktopWheelMode() {
    return window.matchMedia('(min-width: 991px)').matches;
  }

  /**
   * 判断目标是否需要保留原生交互；allowHeader 仅放开导航空白区，不放开页脚、表单、抽屉或内部滚动区。
   * @param {EventTarget} target 当前事件目标。
   * @param {boolean} allowHeader 是否让桌面导航区域参与整屏切换。
   * @returns {boolean} 应跳过整屏切换时返回 true。
   */
  function isIgnoredTarget(target, allowHeader) {
    if (!target || !target.closest) return false;
    if (
      target.closest(HEADER_EXCLUSION_SELECTOR) &&
      (!allowHeader || target.closest('#shopify-section-group-footer-group, .shopify-section-group-footer-group'))
    ) return true;
    if (target.closest(SCROLLABLE_IGNORE_SELECTOR)) return true;

    var el = target;
    while (el && el !== document.body && el !== document.documentElement) {
      if (el === main || (el.id && el.id === 'MainContent')) break;
      if (el.nodeType === 1) {
        var style = window.getComputedStyle(el);
        var overflowY = style.overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          el.scrollHeight > el.clientHeight + 1
        ) {
          return true;
        }
      }
      el = el.parentElement;
    }

    return false;
  }

  /**
   * 判断触摸目标是否为页脚中的表单控件；普通链接/按钮仍允许先记录手势，轻触时不触发切页。
   * @param {EventTarget} target 当前触摸目标。
   * @returns {boolean} 目标为表单或可编辑控件时返回 true。
   */
  function isFooterInteractiveTarget(target) {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest(
        '.shopify-section-group-footer-group input, .shopify-section-group-footer-group textarea, ' +
          '.shopify-section-group-footer-group select, .shopify-section-group-footer-group [contenteditable="true"]'
      )
    );
  }

  /**
   * 允许页脚空白区参与底部反向手势，同时保护表单、弹窗和其他交互区域。
   * @param {EventTarget} target 当前触摸目标。
   * @param {boolean} allowFooter 是否允许页脚空白区参与手势。
   * @returns {boolean} 当前目标应忽略整页切换时返回 true。
   */
  function isTouchIgnoredTarget(target, allowFooter) {
    if (allowFooter && target && target.closest && target.closest('.shopify-section-group-footer-group')) {
      if (isFooterInteractiveTarget(target)) return true;
      if (target.closest(SCROLLABLE_IGNORE_SELECTOR)) return true;
      return false;
    }

    return isIgnoredTarget(target);
  }

  /**
   * 获取当前长内容区可进行原生滚动的边界。
   * 返回 null 表示当前区块没有超出一个视口，不需要特殊处理。
   */
  function sectionScrollBounds(index) {
    var section = sections[index];
    var viewportHeight = window.innerHeight || 1;
    if (!section || section.offsetHeight <= viewportHeight + 2) return null;

    var top = documentTop(section);
    return {
      top: top,
      bottom: top + section.offsetHeight - viewportHeight,
    };
  }

  function currentSectionScrollBounds() {
    return sectionScrollBounds(currentIndex);
  }

  /** 释放上一触摸手势的惯性边界；新输入、重置及区块卸载均显式调用。 */
  function clearNativeLongScroll() {
    if (nativeLongIdleTimer) window.clearTimeout(nativeLongIdleTimer);
    nativeLongIdleTimer = 0;
    nativeLongScroll = null;
  }

  /** 内部浏览完全交给浏览器，仅在越过长区块边界时校正位置。 */
  function constrainNativeLongScroll() {
    var owner = nativeLongScroll;
    if (!owner || !sections[owner.index]) return false;
    var scrollY = window.pageYOffset || window.scrollY || 0;
    var nextY = Math.max(owner.bounds.top, Math.min(owner.bounds.bottom, scrollY));
    var corrected = Math.abs(scrollY - nextY) > 1;
    if (corrected) {
      window.scrollTo({ left: window.pageXOffset || 0, top: nextY, behavior: 'instant' });
    }
    applyIndex(owner.index, false);
    return corrected;
  }

  /** 松手后继续保护惯性，直到滚动静默；矫正产生的新滚动也必须先结束。 */
  function scheduleNativeLongIdle() {
    if (nativeLongIdleTimer) window.clearTimeout(nativeLongIdleTimer);
    nativeLongIdleTimer = 0;
    var owner = nativeLongScroll;
    if (!owner || owner.touching) return;
    var delay = Math.max(1, LONG_SCROLL_IDLE_MS - (Date.now() - owner.lastScrollAt));
    nativeLongIdleTimer = window.setTimeout(function () {
      nativeLongIdleTimer = 0;
      if (nativeLongScroll !== owner || owner.touching) return;
      if (Date.now() - owner.lastScrollAt < LONG_SCROLL_IDLE_MS) {
        scheduleNativeLongIdle();
        return;
      }
      if (constrainNativeLongScroll()) {
        owner.lastScrollAt = Date.now();
        scheduleNativeLongIdle();
        return;
      }
      clearNativeLongScroll();
      onScrollOrResize();
    }, delay);
  }

  function beginNativeLongScroll(state) {
    state.mode = 'long';
    nativeLongScroll = {
      index: state.index,
      bounds: state.bounds,
      touching: true,
      lastScrollAt: Date.now(),
    };
  }

  function finishNativeLongTouch() {
    if (!nativeLongScroll) return;
    nativeLongScroll.touching = false;
    nativeLongScroll.lastScrollAt = Date.now();
    scheduleNativeLongIdle();
    onScrollOrResize();
  }

  /**
   * 判断当前手势是否应继续滚动长内容区，而不是立即切换到相邻区块。
   */
  function canScrollCurrentSection(goingDown, goingUp) {
    var bounds = currentSectionScrollBounds();
    if (!bounds) return false;

    var scrollY = window.pageYOffset || window.scrollY || 0;
    if (goingDown && scrollY < bounds.bottom - 2) return true;
    if (goingUp && scrollY > bounds.top + 2) return true;
    return false;
  }

  /**
   * 将滚轮移动限制在当前长内容区边界内，避免一次大幅滚动越过第二页。
   */
  function handleWheelInsideCurrentSection(event, goingDown, goingUp) {
    var bounds = currentSectionScrollBounds();
    if (!bounds) return false;

    var scrollY = window.pageYOffset || window.scrollY || 0;
    if (goingDown && scrollY < bounds.bottom - 2) {
      if (scrollY + event.deltaY > bounds.bottom) {
        event.preventDefault();
        window.scrollTo(0, bounds.bottom);
      }
      return true;
    }

    if (goingUp && scrollY > bounds.top + 2) {
      if (scrollY + event.deltaY < bounds.top) {
        event.preventDefault();
        window.scrollTo(0, bounds.top);
      }
      return true;
    }

    return false;
  }

  /** 设置切屏冷却时间；桌面滚轮惯性尚未结束时延迟解锁，避免一个长手势连续切屏。 */
  function setLock(duration) {
    locked = true;
    if (lockTimer) window.clearTimeout(lockTimer);
    /** 等待桌面滚轮至少静默一小段时间后释放目标屏，其余输入保留原有冷却时间。 */
    lockTimer = window.setTimeout(function releaseLock() {
      var remaining = DESKTOP_WHEEL_IDLE_MS - (Date.now() - desktopWheelLastAt);
      if (desktopWheelIndex !== null && isDesktopWheelMode() && remaining > 0) {
        lockTimer = window.setTimeout(releaseLock, remaining);
        return;
      }
      locked = false;
      lockTimer = 0;
      desktopWheelIndex = null;
    }, duration || LOCK_MS);
  }

  function applyIndex(index, ending) {
    currentIndex = index;
    ending = Boolean(ending);
    if (appliedIndex === index && appliedEnding === ending) return;
    appliedIndex = index;
    appliedEnding = ending;

    if (document.body) {
      document.body.classList.toggle('scroll-transition-pinned', !ending);
      document.body.classList.toggle('scroll-transition-end', ending);
    }

    for (var i = 0; i < sections.length; i++) {
      var isCurrent = i === index;
      sections[i].classList.toggle('is-scroll-section-active', isCurrent);
      sections[i].setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
    }
  }

  // The homepage loader owns the initial position until its first frame is revealed.
  function resetHomepageSection() {
    if (!active || sections.length < 2) return;
    if (lockTimer) window.clearTimeout(lockTimer);
    lockTimer = 0;
    locked = false;
    desktopWheelIndex = null;
    desktopWheelLastAt = 0;
    touchState = null;
    clearNativeLongScroll();
    applyIndex(0, false);
  }

  function indexFromScroll() {
    var scrollY = window.pageYOffset || window.scrollY || 0;
    var lastIndex = sections.length - 1;
    var firstTop = stackStart();
    var lastBottom = stackEnd();

    if (scrollY <= firstTop + 1) {
      return { index: 0, ending: false };
    }

    if (scrollY >= lastBottom - 2) {
      return { index: lastIndex, ending: true };
    }

    var index = 0;
    for (var i = 0; i < sections.length; i++) {
      if (scrollY >= documentTop(sections[i]) - 2) index = i;
    }

    return { index: index, ending: index >= lastIndex };
  }

  /**
   * 判断当前区块是否为单屏区块且停留在中间位置，需要回到该区块顶部以保持整页切换的完整画面。
   * @param {number} index 当前滚动位置对应的区块索引。
   * @param {boolean} ending 是否已经进入最后一页后的页脚滚动区。
   * @returns {boolean} 需要吸附到区块顶部时返回 true。
   */
  function shouldSnapToSectionTop(index, ending) {
    if (ending || index < 0 || index >= sections.length) return false;

    var section = sections[index];
    var viewportHeight = window.innerHeight || 1;
    if (!section || section.offsetHeight > viewportHeight + 2) return false;

    var scrollY = window.pageYOffset || window.scrollY || 0;
    return Math.abs(scrollY - documentTop(section)) > 2;
  }

  /** 同步可见区块；桌面滚轮锁定期间校准到同一目标屏，防止原生滚动回调绕过切屏锁。 */
  function updateTransitions() {
    if (!active || sections.length < 2) return;
    if (document.documentElement.classList.contains('xtrem-loading')) {
      resetHomepageSection();
      return;
    }
    if (locked && desktopWheelIndex !== null && isDesktopWheelMode()) {
      var targetTop = Math.min(documentScrollMax(), Math.max(0, Math.round(documentTop(sections[desktopWheelIndex]))));
      if (Math.abs((window.pageYOffset || window.scrollY || 0) - targetTop) > 2) {
        window.scrollTo(0, targetTop);
      }
      applyIndex(desktopWheelIndex, desktopWheelIndex >= sections.length - 1);
      return;
    }
    if (nativeLongScroll) {
      constrainNativeLongScroll();
      return;
    }
    var state = indexFromScroll();

    // 快速反向滚动可能直接落在单屏区块中段，此时将其吸附到顶部，避免区块内容仍在视口外而出现白屏。
    if (shouldSnapToSectionTop(state.index, state.ending)) {
      snapToIndex(state.index, true);
      return;
    }

    applyIndex(state.index, state.ending);
  }

  /**
   * 将页面吸附到指定区块顶部；force 参数用于惯性滚动越界时绕过旧手势锁，及时恢复可见内容。
   * @param {number} nextIndex 目标区块索引。
   * @param {boolean} force 是否强制执行吸附。
   * @returns {void}
   */
  function snapToIndex(nextIndex, force) {
    if (!active || sections.length < 2 || (!force && locked)) return;

    var lastIndex = sections.length - 1;
    clearNativeLongScroll();

    if (nextIndex < 0) {
      setLock(400);
      window.scrollTo(0, Math.max(0, stackStart() - 1));
      return;
    }

    if (nextIndex > lastIndex) {
      return;
    }

    setLock(LOCK_MS);
    window.scrollTo(0, Math.max(0, Math.round(documentTop(sections[nextIndex]))));
    applyIndex(nextIndex, nextIndex >= lastIndex);
  }

  /** 记录桌面滚轮本次手势唯一的目标屏，再复用原有切屏实现；不改变移动端的切屏状态。 */
  function snapFromWheel(nextIndex) {
    if (isDesktopWheelMode()) {
      desktopWheelIndex = nextIndex;
      desktopWheelLastAt = Date.now();
    }
    snapToIndex(nextIndex);
  }

  /**
   * 统一桌面导航与内容区的滚轮入口，同一手势只切一屏；长内容区、表单和移动端保留原有处理。
   */
  function onWheel(event) {
    if (!active || sections.length < 2) return;
    var atDocumentEnd = isNearStackEnd();
    var footerTarget =
      event.target && event.target.closest && event.target.closest('.shopify-section-group-footer-group');
    if (isIgnoredTarget(event.target, isDesktopWheelMode()) && !(atDocumentEnd && footerTarget && !isFooterInteractiveTarget(event.target))) {
      return;
    }

    var goingDown = event.deltaY > 0;
    var goingUp = event.deltaY < 0;
    if (!goingDown && !goingUp) return;
    clearNativeLongScroll();

    if (locked) {
      if (desktopWheelIndex !== null && isDesktopWheelMode()) {
        desktopWheelLastAt = Date.now();
        event.preventDefault();
        return;
      }
      if (isInsideStack() || isNearStackEnd()) event.preventDefault();
      return;
    }

    if (handleWheelInsideCurrentSection(event, goingDown, goingUp)) return;

    if (isInsideStack()) {
      if (currentIndex === 0 && goingUp) return;
      event.preventDefault();
      snapFromWheel(goingDown ? currentIndex + 1 : currentIndex - 1);
      return;
    }

    if (goingUp && isAtLastSectionTop()) {
      event.preventDefault();
      snapFromWheel(sections.length - 2);
      return;
    }

    if (goingUp && isNearStackEnd()) {
      event.preventDefault();
      snapFromWheel(sections.length - 1);
    }
  }

  /** 只在手势起点读取目标及长区块边界，移动期间不再遍历 DOM 或测量布局。 */
  function onTouchStart(event) {
    if (!active || sections.length < 2) return;
    if (nativeLongScroll) constrainNativeLongScroll();
    clearNativeLongScroll();
    touchState = null;
    if (!event.touches || event.touches.length !== 1) return;

    var atDocumentEnd = isNearStackEnd();
    if (isTouchIgnoredTarget(event.target, atDocumentEnd)) return;
    var point = event.touches[0];
    touchState = {
      startY: point.clientY,
      startX: point.clientX,
      horizontalTrack: event.target && event.target.closest &&
        event.target.closest('.from-users--slider [data-from-users-slider]'),
      scrollY: window.pageYOffset || window.scrollY || 0,
      index: currentIndex,
      // 最后一屏与页脚继续使用原生文档滚动，不套用内部长画板的底部围栏。
      bounds: currentIndex < sections.length - 1 ? currentSectionScrollBounds() : null,
      insideStack: isInsideStack(),
      atLastSectionTop: currentIndex === sections.length - 1 && isAtLastSectionTop(),
      atDocumentEnd: atDocumentEnd,
      mode: 'pending',
    };
  }

  /** 原生浏览一旦开始便不再 preventDefault；边界向外的新手势才进入切屏模式。 */
  function onTouchMove(event) {
    var state = touchState;
    if (!state || !event.touches || !event.touches[0]) return;
    if (event.touches.length !== 1) {
      touchState = null;
      clearNativeLongScroll();
      return;
    }
    if (state.mode === 'long' || state.mode === 'native' || state.mode === 'horizontal') return;

    if (state.mode === 'pending') {
      var dy = state.startY - event.touches[0].clientY;
      var dx = state.startX - event.touches[0].clientX;
      if (Math.abs(dy) < 2 && Math.abs(dx) < 2) return;
      if (state.horizontalTrack) {
        // Let the review carousel establish intent before cancelling native scrolling.
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        if (Math.abs(dx) >= Math.abs(dy)) {
          state.mode = 'horizontal';
          return;
        }
      }
      var goingDown = dy > 0;
      var goingUp = dy < 0;
      var bounds = state.bounds;
      var insideLongSection = bounds && (
        (goingDown && state.scrollY < bounds.bottom - 2) ||
        (goingUp && state.scrollY > bounds.top + 2) ||
        Math.abs(dx) >= Math.abs(dy)
      );
      // 800ms 冷却只限制切页，当前长画板在冷却期间仍可立即浏览。
      if (insideLongSection) {
        beginNativeLongScroll(state);
        return;
      }

      var shouldCapture = locked || state.insideStack ||
        (state.atLastSectionTop && goingUp) || (state.atDocumentEnd && goingUp);
      if (state.index === 0 && goingUp && !locked) shouldCapture = false;
      // 浏览器已经接管的手势不再尝试中途截获。
      if (!shouldCapture || event.cancelable === false) {
        if (bounds && event.cancelable === false) beginNativeLongScroll(state);
        else state.mode = 'native';
        return;
      }
      state.mode = 'captured';
    }

    if (state.mode === 'captured' && event.cancelable !== false) event.preventDefault();
  }

  /** 一次内部浏览（含惯性）不切页；普通边界手势沿用40px阈值与原页脚返回规则。 */
  function onTouchEnd(event) {
    var state = touchState;
    if (!state) return;
    if (event.touches && event.touches.length) {
      onTouchCancel();
      return;
    }
    var point = event.changedTouches && event.changedTouches[0];
    var dy = state.startY - (point ? point.clientY : state.startY);
    var dx = state.startX - (point ? point.clientX : state.startX);
    touchState = null;
    if (state.mode === 'horizontal') return;
    if (state.mode === 'long') {
      finishNativeLongTouch();
      return;
    }

    if (!active || sections.length < 2) return;
    if (Math.abs(dy) < SWIPE_THRESHOLD || Math.abs(dy) <= Math.abs(dx)) return;

    // 底部反向手势优先于旧切页锁，仍返回最后一页。
    if (state.atDocumentEnd && dy < 0) {
      snapToIndex(sections.length - 1, true);
      return;
    }
    if (locked) return;
    if (canScrollCurrentSection(dy > 0, dy < 0)) return;
    if (state.atLastSectionTop && dy < 0) {
      snapToIndex(sections.length - 2);
      return;
    }
    if (isInsideStack()) {
      if (currentIndex === 0 && dy < 0) return;
      snapToIndex(dy > 0 ? currentIndex + 1 : currentIndex - 1);
      return;
    }
    if (dy < 0 && isAtLastSectionTop()) {
      snapToIndex(sections.length - 2);
      return;
    }
    if (dy < 0 && isNearStackEnd()) snapToIndex(sections.length - 1);
  }

  /** 系统取消触摸不触发切页；已发生的原生惯性仍受原长区块边界保护。 */
  function onTouchCancel() {
    touchState = null;
    finishNativeLongTouch();
  }

  /** 每帧只同步一次；惯性期间只读缓存边界，视口尺寸变化时再刷新边界。 */
  function onScrollOrResize(event) {
    if (event && event.type === 'resize') resizePending = true;
    if (nativeLongScroll && event && event.type === 'scroll') {
      nativeLongScroll.lastScrollAt = Date.now();
      if (!nativeLongScroll.touching) scheduleNativeLongIdle();
    }
    if (transitionFrame) return;
    transitionFrame = window.requestAnimationFrame(function () {
      transitionFrame = 0;
      if (!active) return;
      if (resizePending) {
        resizePending = false;
        if (touchState) touchState.bounds = touchState.index < sections.length - 1 ? sectionScrollBounds(touchState.index) : null;
        if (nativeLongScroll) {
          var bounds = sectionScrollBounds(nativeLongScroll.index);
          if (bounds) nativeLongScroll.bounds = bounds;
          else {
            var section = sections[nativeLongScroll.index];
            if (section) {
              var top = documentTop(section);
              nativeLongScroll.bounds = { top: top, bottom: top };
            } else clearNativeLongScroll();
          }
        }
      }
      updateTransitions();
    });
  }

  function bindListeners() {
    if (listenersBound) return;
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });
    listenersBound = true;
  }

  function unbindListeners() {
    if (!listenersBound) return;
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchcancel', onTouchCancel);
    listenersBound = false;
  }

  /**
   * 清理整页切换状态、事件监听、桌面滚轮目标和触摸缓存，便于主题编辑器安全重载。
   */
  function teardown() {
    document.documentElement.classList.remove('scroll-transition-active');
    if (document.body) {
      document.body.classList.remove('scroll-transition-active');
      document.body.classList.remove('scroll-transition-pinned');
      document.body.classList.remove('scroll-transition-end');
    }

    for (var i = 0; i < sections.length; i++) resetVisual(sections[i]);

    unbindListeners();
    if (lockTimer) window.clearTimeout(lockTimer);
    lockTimer = 0;
    locked = false;
    desktopWheelIndex = null;
    desktopWheelLastAt = 0;
    currentIndex = 0;
    touchState = null;
    clearNativeLongScroll();
    if (transitionFrame) window.cancelAnimationFrame(transitionFrame);
    transitionFrame = 0;
    resizePending = false;
    appliedIndex = null;
    appliedEnding = null;
    active = false;
  }

  function setup() {
    main = getMainContent();
    sections = collectTransitionSections(main);

    if (!main || sections.length < 2) {
      teardown();
      return;
    }

    document.documentElement.classList.add('scroll-transition-active');
    document.body.classList.add('scroll-transition-active');
    active = true;

    bindListeners();
    updateTransitions();
  }

  function init() {
    teardown();

    if (mobileHomeQuery.matches && document.querySelector('[data-xtrem-mobile-home]')) return;

    // 手机编辑预览沿用手势切屏；切回桌面编辑时恢复编辑器原生滚动。
    if (window.Shopify && window.Shopify.designMode) {
      if (!editorMobileQuery) editorMobileQuery = window.matchMedia('(max-width: 767px)');
      if (!editorMobileQuery.matches) return;
    }

    setup();
  }

  function start() {
    init();

    if (shopifyBound) return;
    mobileHomeQuery.addEventListener('change', init);
    if (editorMobileQuery) {
      if (editorMobileQuery.addEventListener) {
        editorMobileQuery.addEventListener('change', init);
      } else {
        editorMobileQuery.addListener(init);
      }
    }
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', init);
    document.addEventListener('shopify:section:reorder', init);
    shopifyBound = true;
  }

  document.addEventListener('xtrem:homepage-reset', resetHomepageSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
